import React, { useState, useMemo } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Chip,
  Grid,
  Pagination,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Tooltip,
  Switch,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { useTopics, useDeleteTopic, useToggleTopicPublish } from "../hooks/useTopics";
import {
  OpinionFilters,
  GENRES,
  GENRE_LABELS,
  Genre,
  DIFFICULTIES,
  DIFFICULTY_LABELS,
  QUESTION_TYPES,
  QUESTION_TYPE_LABELS,
  CLAIM_TYPE_LABELS,
  Opinion,
} from "../types";
import { useAuth } from "../contexts/AuthContext";
import DifficultyChip from "./common/DifficultyChip";
import TopicalityChip from "./common/TopicalityChip";

const TopicList: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // フィルター状態
  const [filters, setFilters] = useState<OpinionFilters>({
    page: 1,
    limit: 20,
    difficulty: undefined,
    genre: undefined,
    isPublished: undefined,
    search: "",
  });

  // UI状態
  const [searchInput, setSearchInput] = useState("");
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; opinion?: Opinion }>({
    open: false,
  });
  const [previewDialog, setPreviewDialog] = useState<{ open: boolean; opinion?: Opinion }>({
    open: false,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  // 一括削除の選択状態
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // データ取得
  const { data, isLoading, error } = useTopics(filters);
  const deleteTopicMutation = useDeleteTopic();
  const toggleTopicPublishMutation = useToggleTopicPublish();

  // 検索実行
  const handleSearch = () => {
    setFilters((prev) => ({
      ...prev,
      search: searchInput,
      page: 1, // 検索時はページをリセット
    }));
  };

  // フィルター変更
  const handleFilterChange = (
    key: keyof OpinionFilters,
    value: string | number | boolean | undefined
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === "" ? undefined : value,
      page: 1, // フィルター変更時はページをリセット
    }));
  };

  // フィルタークリア
  const handleClearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      difficulty: undefined,
      genre: undefined,
      isPublished: undefined,
      questionType: undefined,
      search: "",
    });
    setSearchInput("");
  };

  // ページ変更
  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  // 削除確認ダイアログ
  const handleDeleteClick = (opinion: Opinion) => {
    setDeleteDialog({ open: true, opinion });
  };

  // 削除実行
  const handleDeleteConfirm = async () => {
    if (deleteDialog.opinion?.id) {
      try {
        await deleteTopicMutation.mutateAsync(deleteDialog.opinion.id);
        setDeleteDialog({ open: false });
      } catch (error) {
        console.error("Delete failed:", error);
      }
    }
  };

  // 公開状態切り替え
  const handleTogglePublish = async (opinion: Opinion) => {
    if (opinion.id) {
      // 既にトグル中の場合は無視
      if (togglingIds.has(opinion.id)) return;

      setTogglingIds((prev) => new Set(prev).add(opinion.id!));
      try {
        await toggleTopicPublishMutation.mutateAsync({
          id: opinion.id,
          isPublished: !opinion.isPublished,
        });
      } catch (error) {
        console.error("Toggle publish failed:", error);
      } finally {
        setTogglingIds((prev) => {
          const next = new Set(prev);
          next.delete(opinion.id!);
          return next;
        });
      }
    }
  };

  // 現在ページの問題ID一覧（全選択・一括削除用）
  const pageIds = useMemo(
    () => (data?.data ?? []).map((o: Opinion) => o.id!).filter(Boolean),
    [data]
  );
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

  // 選択のトグル
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // 現在ページを全選択 / 全解除
  const toggleSelectAllPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  // 一括削除の実行
  const handleBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      for (const id of Array.from(selectedIds)) {
        await deleteTopicMutation.mutateAsync(id);
      }
      setSelectedIds(new Set());
      setBulkDeleteDialog(false);
    } catch (error) {
      console.error("Bulk delete failed:", error);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // フィルター適用数
  const appliedFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.difficulty) count++;
    if (filters.genre) count++;
    if (filters.isPublished !== undefined) count++;
    if (filters.questionType) count++;
    if (filters.search) count++;
    return count;
  }, [filters]);

  if (error) {
    return (
      <Alert severity="error">
        データの取得に失敗しました: {error instanceof Error ? error.message : "Unknown error"}
      </Alert>
    );
  }

  return (
    <Box>
      {/* ヘッダー */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexDirection={{ xs: "column", sm: "row" }}
        gap={2}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
        >
          問題一覧
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          width={{ xs: "100%", sm: "auto" }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/opinions/new"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            新規作成
          </Button>
        </Stack>
      </Box>

      {/* 検索・フィルターエリア */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* 検索バー */}
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                fullWidth
                placeholder="テーマ、主張、本文で検索..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={handleSearch} disabled={isLoading}>
                      <SearchIcon />
                    </IconButton>
                  ),
                }}
              />
            </Grid>

            {/* フィルター切り替え */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box
                display="flex"
                justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                gap={1}
                flexWrap="wrap"
              >
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  フィルター
                  {appliedFiltersCount > 0 && (
                    <Chip label={appliedFiltersCount} size="small" color="primary" sx={{ ml: 1 }} />
                  )}
                </Button>
                {appliedFiltersCount > 0 && (
                  <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleClearFilters}>
                    クリア
                  </Button>
                )}
              </Box>
            </Grid>

            {/* フィルター詳細 */}
            {showFilters && (
              <Grid size={12}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControl fullWidth>
                      <InputLabel>難易度</InputLabel>
                      <Select
                        value={filters.difficulty || ""}
                        onChange={(e) => handleFilterChange("difficulty", e.target.value)}
                        label="難易度"
                      >
                        <MenuItem value="">すべて</MenuItem>
                        {DIFFICULTIES.map((diff) => (
                          <MenuItem key={diff} value={diff}>
                            難易度{diff} - {DIFFICULTY_LABELS[diff]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControl fullWidth>
                      <InputLabel>ジャンル</InputLabel>
                      <Select
                        value={filters.genre || ""}
                        onChange={(e) => handleFilterChange("genre", e.target.value)}
                        label="ジャンル"
                      >
                        <MenuItem value="">すべて</MenuItem>
                        {GENRES.map((genre) => (
                          <MenuItem key={genre} value={genre}>
                            {GENRE_LABELS[genre]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControl fullWidth>
                      <InputLabel>公開状態</InputLabel>
                      <Select
                        value={
                          filters.isPublished === undefined ? "" : filters.isPublished.toString()
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          handleFilterChange(
                            "isPublished",
                            value === "" ? undefined : value === "true"
                          );
                        }}
                        label="公開状態"
                      >
                        <MenuItem value="">すべて</MenuItem>
                        <MenuItem value="true">公開済み</MenuItem>
                        <MenuItem value="false">非公開</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControl fullWidth>
                      <InputLabel>問題タイプ</InputLabel>
                      <Select
                        value={filters.questionType || ""}
                        onChange={(e) => handleFilterChange("questionType", e.target.value)}
                        label="問題タイプ"
                      >
                        <MenuItem value="">すべて</MenuItem>
                        {QUESTION_TYPES.map((type) => (
                          <MenuItem key={type} value={type}>
                            {QUESTION_TYPE_LABELS[type]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* ローディング表示 */}
      {isLoading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {/* 問題リスト */}
      {data && !isLoading && (
        <>
          {/* 件数表示 */}
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {data.pagination.total}件中 {(data.pagination.page - 1) * data.pagination.limit + 1} -{" "}
            {Math.min(data.pagination.page * data.pagination.limit, data.pagination.total)}件を表示
          </Typography>

          {/* 一括操作バー（管理者のみ） */}
          {isAdmin && data.data.length > 0 && (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              mb={1}
              flexWrap="wrap"
              sx={{ bgcolor: "grey.50", borderRadius: 1, px: 1, py: 0.5 }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allPageSelected}
                    indeterminate={!allPageSelected && pageIds.some((id) => selectedIds.has(id))}
                    onChange={toggleSelectAllPage}
                  />
                }
                label="このページを全選択"
              />
              {selectedIds.size > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {selectedIds.size}件選択中
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={() => setBulkDeleteDialog(true)}
                  >
                    選択した問題を削除
                  </Button>
                  <Button size="small" onClick={() => setSelectedIds(new Set())}>
                    選択解除
                  </Button>
                </>
              )}
            </Box>
          )}

          {/* 問題カード一覧 */}
          <Grid container spacing={2}>
            {data.data.map((opinion: Opinion) => (
              <Grid size={12} key={opinion.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      {isAdmin && (
                        <Checkbox
                          checked={selectedIds.has(opinion.id!)}
                          onChange={() => toggleSelect(opinion.id!)}
                          sx={{ mr: 1, mt: -0.5 }}
                          inputProps={{ "aria-label": "この問題を選択" }}
                        />
                      )}
                      {/* 問題情報 */}
                      <Box flex={1}>
                        <Box display="flex" gap={1} mb={1}>
                          <DifficultyChip difficulty={opinion.difficulty} />
                          <Chip
                            label={GENRE_LABELS[opinion.genre as Genre] || opinion.genre}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                          {opinion.questionType && (
                            <Tooltip title={QUESTION_TYPE_LABELS[opinion.questionType]}>
                              <Chip
                                label={QUESTION_TYPE_LABELS[opinion.questionType]}
                                size="small"
                                variant="outlined"
                              />
                            </Tooltip>
                          )}
                          {opinion.questionType === "claim" && opinion.claimType && (
                            <Chip
                              label={CLAIM_TYPE_LABELS[opinion.claimType]}
                              size="small"
                              variant="outlined"
                              color="info"
                            />
                          )}
                          <TopicalityChip isTopical={opinion.isTopical} />
                        </Box>

                        {/* テーマ */}
                        {opinion.theme && (
                          <Typography variant="h6" gutterBottom>
                            {opinion.theme}
                          </Typography>
                        )}

                        <Typography
                          variant="body2"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {opinion.sourceText}
                        </Typography>

                        {opinion.background && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              mt: 0.5,
                            }}
                          >
                            背景: {opinion.background}
                          </Typography>
                        )}

                        <Box mt={1}>
                          <Tooltip
                            title={!isAdmin ? "公開設定は管理者のみが変更できます" : ""}
                            placement="right"
                          >
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={opinion.isPublished}
                                  onChange={() => handleTogglePublish(opinion)}
                                  disabled={togglingIds.has(opinion.id!) || !isAdmin}
                                />
                              }
                              label={opinion.isPublished ? "公開中" : "非公開"}
                            />
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* アクション */}
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="プレビュー">
                          <IconButton
                            size="small"
                            onClick={() => setPreviewDialog({ open: true, opinion })}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="編集">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/opinions/edit/${opinion.id}`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>

                        {isAdmin && (
                          <Tooltip title="削除">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(opinion)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* データが0件の場合 */}
          {data.data.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="textSecondary">
                条件に一致する問題が見つかりませんでした
              </Typography>
              <Button variant="outlined" onClick={handleClearFilters} sx={{ mt: 2 }}>
                フィルターをクリア
              </Button>
            </Box>
          )}

          {/* ページネーション */}
          {data.pagination.pages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={data.pagination.pages}
                page={data.pagination.page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}

      {/* プレビューダイアログ */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false })}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
      >
        <DialogTitle>問題の詳細</DialogTitle>
        <DialogContent>
          {previewDialog.opinion && (
            <Box>
              {/* メタ情報 */}
              <Box display="flex" gap={1} mb={2}>
                <DifficultyChip difficulty={previewDialog.opinion.difficulty} />
                <Chip
                  label={
                    GENRE_LABELS[previewDialog.opinion.genre as Genre] ||
                    previewDialog.opinion.genre
                  }
                  size="small"
                  color="secondary"
                />
                {previewDialog.opinion.questionType && (
                  <Chip
                    label={QUESTION_TYPE_LABELS[previewDialog.opinion.questionType]}
                    size="small"
                    variant="outlined"
                  />
                )}
                {previewDialog.opinion.questionType === "claim" &&
                  previewDialog.opinion.claimType && (
                    <Chip
                      label={CLAIM_TYPE_LABELS[previewDialog.opinion.claimType]}
                      size="small"
                      variant="outlined"
                      color="info"
                    />
                  )}
                <Chip
                  label={previewDialog.opinion.isPublished ? "公開中" : "非公開"}
                  size="small"
                  color={previewDialog.opinion.isPublished ? "success" : "default"}
                />
                <TopicalityChip isTopical={previewDialog.opinion.isTopical} showAllStates />
              </Box>

              {/* テーマ */}
              {previewDialog.opinion.theme && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    テーマ
                  </Typography>
                  <Typography variant="h6">{previewDialog.opinion.theme}</Typography>
                </Box>
              )}

              {/* 背景説明 */}
              {previewDialog.opinion.background && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    背景説明
                  </Typography>
                  <Typography>{previewDialog.opinion.background}</Typography>
                </Box>
              )}

              {/* 主張 */}
              {previewDialog.opinion.claim && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    主張
                  </Typography>
                  <Typography>{previewDialog.opinion.claim}</Typography>
                </Box>
              )}

              {/* 本文 */}
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  本文
                </Typography>
                <Typography style={{ whiteSpace: "pre-wrap" }}>
                  {previewDialog.opinion.sourceText}
                </Typography>
              </Box>

              {/* 意見パターン */}
              {previewDialog.opinion.opinionPatterns &&
                previewDialog.opinion.opinionPatterns.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      意見パターン
                    </Typography>
                    {previewDialog.opinion.opinionPatterns.map((pattern, index) => (
                      <Box key={index} mb={1}>
                        <Typography variant="body2" fontWeight="bold">
                          {pattern.typeLabel}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {pattern.description}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                          例: {pattern.example}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

              {/* メタデータ */}
              <Box mt={3} pt={2} borderTop={1} borderColor="divider">
                <Typography variant="caption" color="textSecondary" display="block">
                  作成日:{" "}
                  {previewDialog.opinion.createdAt
                    ? new Date(previewDialog.opinion.createdAt).toLocaleString("ja-JP")
                    : "不明"}
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block">
                  更新日:{" "}
                  {previewDialog.opinion.updatedAt
                    ? new Date(previewDialog.opinion.updatedAt).toLocaleString("ja-JP")
                    : "不明"}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog({ open: false })}>閉じる</Button>
          {previewDialog.opinion && (
            <Button
              variant="contained"
              onClick={() => {
                setPreviewDialog({ open: false });
                navigate(`/opinions/edit/${previewDialog.opinion?.id}`);
              }}
              startIcon={<EditIcon />}
            >
              編集
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false })}>
        <DialogTitle>問題の削除</DialogTitle>
        <DialogContent>
          <Typography>この問題を削除してもよろしいですか？</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            この操作は取り消すことができません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false })}
            disabled={deleteTopicMutation.isLoading}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={deleteTopicMutation.isLoading}
          >
            {deleteTopicMutation.isLoading ? "削除中..." : "削除"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 一括削除確認ダイアログ */}
      <Dialog open={bulkDeleteDialog} onClose={() => !isBulkDeleting && setBulkDeleteDialog(false)}>
        <DialogTitle>問題の一括削除</DialogTitle>
        <DialogContent>
          <Typography>選択した {selectedIds.size} 件の問題を削除してもよろしいですか？</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            この操作は取り消すことができません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialog(false)} disabled={isBulkDeleting}>
            キャンセル
          </Button>
          <Button
            onClick={handleBulkDelete}
            color="error"
            variant="contained"
            disabled={isBulkDeleting}
          >
            {isBulkDeleting ? "削除中..." : `${selectedIds.size}件を削除`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TopicList;
